import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ResolvedRating } from '@/ratings/useRiskRatingResolver';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import { getFriendlyId } from '@/utils/friendlyId';

import { UNRATED } from '../controls/lookupData';
import type {
  EnterpriseRiskFields,
  EnterpriseRiskRegisterFields,
} from './types';

const numberFormats = new Map<string, Intl.NumberFormat>();

const getNumberFormat = (
  locale: string,
  fractionDigits: number
): Intl.NumberFormat => {
  const key = `${locale}/${fractionDigits}`;
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: false,
    });
    numberFormats.set(key, format);
  }

  return format;
};

/**
 * Renders a likelihood/impact score for display: whole numbers bare, anything
 * else to one decimal place, with the decimal separator of `locale` (the
 * i18next language, so it follows the UI rather than the browser).
 */
export const formatDimValue = (value: number, locale: string): string =>
  getNumberFormat(locale, Number.isInteger(value) ? 0 : 1).format(value);

/**
 * An unassessed likelihood/impact is persisted as `0`, not as null — and no
 * declared rating value is ever zero or negative (see the `isAssessedScore`
 * rule in `packages/i18n/src/ratings.ts`), so a non-positive dimension is the
 * sentinel rather than a score. Normalising it here, at the one point every
 * cell, label and export twin reads a stored dimension through, is what keeps
 * a CSV from carrying `0` where the screen shows `-`.
 */
export const assessedDimValue = (
  value: number | null | undefined
): null | number => (value != null && value > 0 ? value : null);

/**
 * A dimension score for machine-readable export (CSV): the number itself, so
 * the column is unambiguous whatever the locale and a spreadsheet sorts it
 * numerically. Rounded through the display formatter so the export never
 * disagrees with the screen in its last digit.
 */
export const exportDimValue = (
  value: number | null | undefined
): null | number => {
  const assessed = assessedDimValue(value);

  return assessed == null ? null : Number(formatDimValue(assessed, 'en'));
};

/** A dimension score for human-readable export (PDF): as the screen shows it. */
export const pdfDimValue = (
  value: number | null | undefined,
  locale: string
): null | string => {
  const assessed = assessedDimValue(value);

  return assessed == null ? null : formatDimValue(assessed, locale);
};

export type DimRating = Pick<ResolvedRating, 'color' | 'label'>;

/**
 * A dimension score with its rating: `Unlikely (2.5)`. A score the band rule
 * cannot place takes the platform's unrated badge, so the number is never
 * shown beside an empty label.
 */
export const resolveDimRating = (
  value: number,
  locale: string,
  resolve: (value: number) => DimRating | undefined
): DimRating => {
  const rating = resolve(value) ?? UNRATED;

  return {
    color: rating.color,
    label: `${rating.label} (${formatDimValue(value, locale)})`,
  };
};

/**
 * Sort comparator for a nullable dimension score. Matches the order Cloudscape
 * gives a numeric `sortingField` (which the rating columns use): scores by
 * magnitude, treating unscored as the lowest value — so unscored rows group
 * first ascending and last descending, and never interleave with scored ones.
 */
export const compareDimValues = (
  a: number | null | undefined,
  b: number | null | undefined
): number => {
  if (a == null || b == null) {
    return a == null ? (b == null ? 0 : -1) : 1;
  }

  return a - b;
};

const dimLabel = (
  value: number | null | undefined,
  locale: string,
  resolve: (value: number) => DimRating | undefined
): null | string => {
  const assessed = assessedDimValue(value);

  return assessed == null
    ? '-'
    : resolveDimRating(assessed, locale, resolve).label;
};

export const useLabelledFields = (
  data: EnterpriseRiskFields[] | undefined
): EnterpriseRiskRegisterFields[] => {
  const { t, i18n } = useTranslation(['common']);
  const locale = i18n.language;
  const tiers = useMemo(() => t('tiers', { returnObjects: true }), [t]);
  const treatments = useMemo(
    () => t('treatments', { returnObjects: true }),
    [t]
  );
  const { getByValue: getResidualRating } = useRating('risk_controlled');
  const { getByValue: getInherentRating } = useRating('risk_uncontrolled');
  const { resolveLikelihood, resolveImpact } = useRiskRatingResolver(
    'standard',
    { configType: 'risk_assessment' }
  );

  return useMemo(() => {
    return (
      data?.map((record) => ({
        ...record,
        SequentialIdLabelled: record.SequentialId
          ? getFriendlyId(Parent_Type_Enum.EnterpriseRisk, record.SequentialId)
          : '-',
        TierLabelled: tiers[String(record.Tier) as keyof typeof tiers],
        TreatmentLabelled:
          treatments[String(record.Treatment) as keyof typeof treatments],
        ParentTitle: record.parent?.Title ?? '-',
        CreatedByUser: record.createdByUser?.FriendlyName ?? '-',
        ModifiedByUser: record.modifiedByUser?.FriendlyName ?? '-',
        Description: record.Description ?? '-',
        InherentMeanLabelled:
          getInherentRating(record.score?.InherentRatingMean)?.label ?? '-',
        InherentWorstCaseLabelled:
          getInherentRating(record.score?.InherentRatingWorstCase)?.label ??
          '-',
        ResidualMeanLabelled:
          getResidualRating(record.score?.ResidualRatingMean)?.label ?? '-',
        ResidualWorstCaseLabelled:
          getResidualRating(record.score?.ResidualRatingWorstCase)?.label ??
          '-',
        InherentLikelihoodMeanLabelled: dimLabel(
          record.score?.InherentLikelihoodMean,
          locale,
          resolveLikelihood
        ),
        InherentImpactMeanLabelled: dimLabel(
          record.score?.InherentImpactMean,
          locale,
          resolveImpact
        ),
        ResidualLikelihoodMeanLabelled: dimLabel(
          record.score?.ResidualLikelihoodMean,
          locale,
          resolveLikelihood
        ),
        ResidualImpactMeanLabelled: dimLabel(
          record.score?.ResidualImpactMean,
          locale,
          resolveImpact
        ),
        InherentLikelihoodMedianLabelled: dimLabel(
          record.score?.InherentLikelihoodMedian,
          locale,
          resolveLikelihood
        ),
        InherentImpactMedianLabelled: dimLabel(
          record.score?.InherentImpactMedian,
          locale,
          resolveImpact
        ),
        ResidualLikelihoodMedianLabelled: dimLabel(
          record.score?.ResidualLikelihoodMedian,
          locale,
          resolveLikelihood
        ),
        ResidualImpactMedianLabelled: dimLabel(
          record.score?.ResidualImpactMedian,
          locale,
          resolveImpact
        ),
        InherentLikelihoodWorstCaseLabelled: dimLabel(
          record.score?.InherentLikelihoodWorstCase,
          locale,
          resolveLikelihood
        ),
        InherentImpactWorstCaseLabelled: dimLabel(
          record.score?.InherentImpactWorstCase,
          locale,
          resolveImpact
        ),
        ResidualLikelihoodWorstCaseLabelled: dimLabel(
          record.score?.ResidualLikelihoodWorstCase,
          locale,
          resolveLikelihood
        ),
        ResidualImpactWorstCaseLabelled: dimLabel(
          record.score?.ResidualImpactWorstCase,
          locale,
          resolveImpact
        ),
      })) ?? []
    );
  }, [
    data,
    locale,
    tiers,
    treatments,
    getInherentRating,
    getResidualRating,
    resolveLikelihood,
    resolveImpact,
  ]);
};
